import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { advocaciaProcessos } from '../../shared/data/advocaciaData';
import { Scale, Clock, AlertTriangle, FileText, Search, Bell, Plus, ShieldCheck, CheckCircle2 } from 'lucide-react';

export const LawSystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans flex flex-col selection:bg-[#0F172A] selection:text-white">
      
      {/* Top Navbar Legal System - STRICT NO SHADOWS */}
      <header className="h-20 bg-[#0F172A] border-b border-slate-800 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-slate-800 text-[#C5A880] flex items-center justify-center font-bold border border-slate-700">
               <Scale className="w-5 h-5" />
            </div>
            <div>
               <h1 className="font-heading font-bold text-lg text-white tracking-wide uppercase">LawControl — {prospect.trade_name}</h1>
               <p className="text-[10px] text-slate-400 font-medium">Gestão de Processos & Prazos Fatais OAB</p>
            </div>
         </div>

         <div className="flex items-center gap-4">
            <div className="relative hidden md:block">
               <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
               <input type="text" placeholder="Buscar por nº de processo ou cliente..." className="pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-xl text-xs text-white outline-none w-72 placeholder:text-slate-500" />
            </div>
            <button className="bg-[#C5A880] text-[#0F172A] px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 hover:bg-amber-400 transition-colors">
               <Plus className="w-4 h-4" /> Novo Processo
            </button>
         </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 p-8 overflow-y-auto">
         
         {/* Metrics Summary - STRICT NO SHADOWS */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Processos Ativos</span>
               <span className="text-3xl font-heading font-bold text-[#0F172A]">142</span>
               <p className="text-xs text-emerald-600 font-medium mt-2">✓ 100% dentro do prazo</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Prazos Fatais (48h)</span>
               <span className="text-3xl font-heading font-bold text-amber-600">3</span>
               <p className="text-xs text-amber-600 font-medium mt-2">⚠️ Requer atenção urgente</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Audiências na Semana</span>
               <span className="text-3xl font-heading font-bold text-[#0F172A]">5</span>
               <p className="text-xs text-slate-500 font-medium mt-2">TJSP & TRF-3</p>
            </div>
            <div className="bg-white p-6 rounded-xl border border-slate-200">
               <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Honorários em Fase de Sucumbência</span>
               <span className="text-3xl font-heading font-bold text-[#C5A880]">R$ 480k</span>
               <p className="text-xs text-slate-500 font-medium mt-2">Expectativa Q4 2026</p>
            </div>
         </div>

         {/* Tabela de Processos - STRICT NO SHADOWS */}
         <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center bg-slate-50">
               <h3 className="font-heading font-bold text-lg text-[#0F172A]">Ações Judiciais Sob Acompanhamento</h3>
               <span className="text-xs font-bold text-slate-500">Filtrado por: Todos os Sócios</span>
            </div>

            <div className="overflow-x-auto">
               <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 border-b border-slate-200 text-slate-600 font-bold uppercase tracking-wider">
                     <tr>
                        <th className="p-4">Nº do Processo / Tribunal</th>
                        <th className="p-4">Cliente / Parte</th>
                        <th className="p-4">Assunto</th>
                        <th className="p-4">Status Atual</th>
                        <th className="p-4">Próximo Prazo Fatal</th>
                     </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                     {advocaciaProcessos.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50 transition-colors">
                           <td className="p-4 font-mono font-bold text-[#0F172A]">
                              {p.processNumber}
                              <span className="block text-[10px] text-slate-400 font-sans font-normal mt-0.5">{p.court}</span>
                           </td>
                           <td className="p-4 font-bold text-slate-800">{p.clientName}</td>
                           <td className="p-4 text-slate-600">{p.subject}</td>
                           <td className="p-4">
                              <span className="bg-slate-100 text-slate-800 border border-slate-300 px-2.5 py-1 rounded-md text-[10px] font-bold">
                                 {p.status}
                              </span>
                           </td>
                           <td className="p-4 font-bold text-amber-700">{p.nextDeadline}</td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </div>

      </main>

    </div>
  );
};
